<script lang="ts" setup>
import { defineProps, ref, computed, defineEmits, PropType } from "vue";
import { authStore } from "@/store/auth";
import Precautions from "@/components/organisms/orders/ApplicationReminderModal/Precautions.vue";
import HowToCancel from "@/components/organisms/orders/ApplicationReminderModal/HowToCancel.vue";
import ApplicationConfirmation from "@/components/organisms/orders/ApplicationReminderModal/ApplicationConfirmation.vue";
import { Application } from "@/types/order/Application";
import { useCustomRouter } from "@/utils/router";

type PageType = "Precautions" | "HowToCancel" | "ApplicationConfirmation";

const props = defineProps({
  applicationToRemind: {
    type: Object as PropType<Application>,
    required: true
  }
});
const isShown = computed(() => !!props.applicationToRemind);
const user = computed(() => authStore.user!); // このモーダルはログイン済みユーザーにしか表示しない想定
const page = ref<PageType>("Precautions");
const emit = defineEmits(["close-hook"]);
const { pushIgnoreError } = useCustomRouter();

/**
 * モーダル内のキャンセル方法ページへ進む
 */
const proceedCancel = () => {
  page.value = "HowToCancel";
};

/**
 * モーダル内の応募確認ページへ進む
 */
const proceedApply = () => {
  page.value = "ApplicationConfirmation";
};

/**
 * モーダル内のページを戻る
 */
const back = () => {
  page.value = "Precautions";
};

const isPagePrecautions = computed(() => page.value === "Precautions");
const isPageHowToCancel = computed(() => page.value === "HowToCancel");
const isPageApplicationConfirmation = computed(
  () => page.value === "ApplicationConfirmation"
);

/**
 * 応募のキャンセル画面へ進む
 */
const cancelApplication = () => {
  emit("close-hook", "cancel");
  pushIgnoreError({
    name: "OrdersCancelPolicyDisplay",
    query: {
      applicationId: String(props.applicationToRemind!.id)
    }
  });
};

/**
 * 応募を確定する
 */
const confirmApplication = () => {
  emit("close-hook", "confirm");
};
</script>

<template>
  <BModal
    :visible="isShown"
    no-fade
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
    centered
    hide-header
    body-class="modal-body"
    hide-footer
  >
    <template v-if="isPagePrecautions">
      <Precautions
        :user="user"
        :applicationToRemind="applicationToRemind"
        @cancel="proceedCancel"
        @apply="proceedApply"
      />
    </template>
    <template v-else-if="isPageHowToCancel">
      <HowToCancel @cancel="cancelApplication" @back="back" />
    </template>
    <template v-else-if="isPageApplicationConfirmation">
      <ApplicationConfirmation
        @confirm="confirmApplication"
        @back="back"
        :workerLastName="user.lastName"
      />
    </template>
  </BModal>
</template>

<style lang="scss" scoped>
.modal-body {
  padding: 0;
}
</style>
